/** Weekday names stored on BTS Task.Day. */
const TASK_DAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

const TWENTY_FOUR_HOURS_MS = 24 * 60 * 60 * 1000;

export function taskSlotsFromSite(site) {
    const raw = site?.taskSlots ?? site?.TaskSlots;
    return Array.isArray(raw) ? raw : [];
}

function parseClock(runAt) {
    const match = String(runAt ?? "").match(/(\d{1,2}):(\d{2})(?::(\d{2}))?/);
    if (!match) return null;
    return {
        hours: Number(match[1]),
        minutes: Number(match[2]),
        seconds: Number(match[3] || 0),
    };
}

/** SQL datetimes without a zone are wall-clock values, same as Task.RunAt. */
function parseWallClock(value) {
    if (value == null || value === "") return null;
    if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
    const text = String(value).trim();
    const match = text.match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?/);
    const hasZone = /[zZ]$|[+-]\d{2}:\d{2}$/.test(text);
    if (match && !hasZone) {
        return new Date(
            Number(match[1]),
            Number(match[2]) - 1,
            Number(match[3]),
            Number(match[4]),
            Number(match[5]),
            Number(match[6] || 0),
        );
    }
    const parsed = new Date(text);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function truncateToSeconds(date) {
    return Math.floor(date.getTime() / 1000) * 1000;
}

/** Date when known, null when the site has no scan, undefined when this record doesn't carry a timestamp. */
function resolveLastScanDate(siteInfo) {
    if (siteInfo.lastScanDate instanceof Date) {
        return Number.isNaN(siteInfo.lastScanDate.getTime()) ? null : siteInfo.lastScanDate;
    }
    if (siteInfo.lastScanDate) {
        return parseWallClock(siteInfo.lastScanDate);
    }
    const raw = siteInfo.lastScan;
    if (raw == null || raw === "" || raw === "N/A") return null;
    if (String(raw).includes("T")) return parseWallClock(raw);
    return undefined;
}

/**
 * Why the sites-list last scan date should flash.
 * An autoscan is a Task row for today's weekday. Sites with no task that day do not flash
 * just because last scan is not from today.
 * Returns zero, one, or both of:
 * - last scan is over 24 hours old (only when an autoscan is scheduled today)
 * - today's comparison email slot has passed and Task.RanOn was not stamped for it
 */
export function getLastScanAlertReasons(siteInfo, now = new Date()) {
    if (!siteInfo || siteInfo.isCcr || siteInfo.isHms) return [];

    const slots = taskSlotsFromSite(siteInfo);
    if (!slots.length) return [];

    const todayName = TASK_DAYS[now.getDay()];
    const todaySlots = slots.filter(
        (slot) => String(slot?.day ?? slot?.Day ?? "").trim().toLowerCase() === todayName.toLowerCase(),
    );
    if (!todaySlots.length) return [];

    const reasons = [];
    const lastScan = resolveLastScanDate(siteInfo);
    if (lastScan === null || (lastScan && now.getTime() - lastScan.getTime() > TWENTY_FOUR_HOURS_MS)) {
        reasons.push("Last scan is over 24 hours old.");
    }

    const dueEmail = todaySlots
        .filter((slot) => !!(slot?.emailReport ?? slot?.EmailReport))
        .map((slot) => {
            const clock = parseClock(slot?.runAt ?? slot?.RunAt);
            if (!clock) return null;
            const slotAt = new Date(
                now.getFullYear(),
                now.getMonth(),
                now.getDate(),
                clock.hours,
                clock.minutes,
                clock.seconds,
            );
            return { slot, slotAt };
        })
        .filter((entry) => entry && truncateToSeconds(entry.slotAt) <= truncateToSeconds(now))
        .sort((a, b) => b.slotAt.getTime() - a.slotAt.getTime())[0];

    if (dueEmail) {
        const ranOn = parseWallClock(dueEmail.slot?.ranOn ?? dueEmail.slot?.RanOn);
        const emailed = ranOn && truncateToSeconds(ranOn) >= truncateToSeconds(dueEmail.slotAt);
        if (!emailed) {
            reasons.push("Comparison report was not emailed.");
        }
    }

    return reasons;
}

export function lastScanAlertTooltip(reasons) {
    return (reasons || []).join(" ");
}
